import { FieldOptionBase } from 'module/api/field/types';
import { useTranslate, useTranslation } from 'module/hooks/use-translation';
import { Tag, type Color } from '@rocketlane/ui';
import { drop, take } from 'ramda';
import { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { renderFieldEditorUserAvatar } from './field-editor-user-avatar';

export const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
`;

export const Placeholder = styled.span`
  padding-left: var(--spacing-03);
`;

export interface TriggerOptionProps {
  option: FieldOptionBase<number>;
}

const toPackageColor = (
  color: FieldOptionBase<number>['color'],
): Color | undefined => (color === 'white' ? 'outline' : color);

export const UserTriggerOption: FC<
  React.PropsWithChildren<TriggerOptionProps>
> = ({ option }) => (
  <Tag
    avatar={renderFieldEditorUserAvatar(option)}
    className="cursor-pointer max-w-[22ch]"
    color={toPackageColor(option.color)}
    data-cy={`tag-value-${option.label}`}
    variant="basic"
  >
    {option.label}
  </Tag>
);
export const TagTriggerOption: FC<
  React.PropsWithChildren<TriggerOptionProps>
> = ({ option: { color, label } }) => (
  <Tag
    className="cursor-pointer max-w-[22ch]"
    color={toPackageColor(color)}
    variant="basic"
  >
    {label}
  </Tag>
);

export type RenderFunction = (
  items: ReactNode[],
  show: number,
  placeholder: ReactNode,
  translate: ReturnType<typeof useTranslate>,
) => ReactNode;

type Props = {
  items: ReactNode[];
  show?: number;
  placeholder?: ReactNode;
  render?: RenderFunction;
};

const defaultRenderFunction: RenderFunction = (
  items,
  show,
  placeholder,
  translate,
) => {
  const count = drop(show)(items).length;
  const other = translate('editor.plus_n_more', { count });

  return (
    <Wrapper>
      {items.length === 0 ? (
        <Placeholder>{placeholder}</Placeholder>
      ) : (
        <>
          {take(show)(items)}
          {count === 0 ? null : (
            <Tag className="cursor-pointer" color="cyan" variant="basic">
              {other}
            </Tag>
          )}
        </>
      )}
    </Wrapper>
  );
};

export const TriggerContent: FC<React.PropsWithChildren<Props>> = ({
  items,
  show = 2,
  placeholder,
  render = defaultRenderFunction,
}) => {
  const fallback_placeholder = useTranslation('select_with_ellipsis');
  const translate = useTranslate();

  return (
    <>{render(items, show, placeholder ?? fallback_placeholder, translate)}</>
  );
};
